import React, { useEffect, useState } from 'react';
import axios from 'axios';

function SectionPeriodList({ blockSchedule, gradeLevel, refreshList }) {
    const [sectionPeriods, setSectionPeriods] = useState([]);
    const [editingPeriod, setEditingPeriod] = useState(null);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [daysOfWeek, setDaysOfWeek] = useState([]);
    const [homeroom, setHomeroom] = useState(false);
    const [periodType, setPeriodType] = useState('regular');

    useEffect(() => {
        if (blockSchedule && gradeLevel) {
            axios
                .get(`/schedules/api/section-periods/get_with_associations/?block_schedule=${blockSchedule}&grade_level=${gradeLevel}&context=management`)
                .then(response => {
                    // Sort section periods by start time chronologically
                    const sortedPeriods = response.data
                        .map(sp => ({ ...sp, homeroom: sp.homeroom || false }))
                        .sort((a, b) => {
                            // Compare start times as strings (HH:MM format)
                            return a.start_time.localeCompare(b.start_time);
                        });
                    setSectionPeriods(sortedPeriods);
                })
                .catch(error => console.error('Error fetching section periods:', error));
        }
    }, [blockSchedule, gradeLevel, refreshList]);

    const startEdit = (sectionPeriod) => {
        setEditingPeriod(sectionPeriod.id);
        setStartTime(sectionPeriod.start_time);
        setEndTime(sectionPeriod.end_time);
        setDaysOfWeek(sectionPeriod.days_of_week);
        setHomeroom(sectionPeriod.homeroom);
        setPeriodType(sectionPeriod.period_type || 'regular');
    };

    const handleDaysOfWeekChange = (day) => {
        if (daysOfWeek.includes(day)) {
            setDaysOfWeek(daysOfWeek.filter(d => d !== day));
        } else {
            setDaysOfWeek([...daysOfWeek, day]);
        }
    };

    const handleEditSubmit = (e, id) => {
        e.preventDefault();

        const updatedPeriod = {
            start_time: startTime,
            end_time: endTime,
            days_of_week: daysOfWeek,
            block_schedule: blockSchedule,
            grade_level: periodType === 'cte_flexible' ? null : gradeLevel,
            homeroom: homeroom, // Include homeroom
            period_type: periodType, // Include period_type
        };

        axios
            .put(`/schedules/api/section-periods/${id}/`, updatedPeriod, {
                headers: {
                    'X-CSRFToken': getCSRFToken(),
                },
                withCredentials: true,
            })
            .then((response) => {
                setEditingPeriod(null);
                // Update the section periods array and re-sort to maintain chronological order
                const updatedPeriods = sectionPeriods.map(sp => (sp.id === id ? { ...sp, ...response.data } : sp));
                const sortedPeriods = updatedPeriods.sort((a, b) => a.start_time.localeCompare(b.start_time));
                setSectionPeriods(sortedPeriods);
            })
            .catch(error => console.error('Error updating section period:', error));
    };

    const deleteSectionPeriod = (id) => {
        axios
            .delete(`/schedules/api/section-periods/${id}/`, {
                headers: {
                    'X-CSRFToken': getCSRFToken(),
                },
                withCredentials: true,
            })
            .then(() => {
                setSectionPeriods(sectionPeriods.filter(sp => sp.id !== id));
            })
            .catch(error => console.error('Error deleting section period:', error));
    };

    return (
        <div>
            <div style={{ width: '100%', overflowX: 'auto' }}>
            <table style={{ minWidth: 600 }}>
                <thead>
                    <tr>{[
                        'Days of Week',
                        'Start Time',
                        'End Time',
                        'Period Type',
                        'Homeroom',
                        'Actions',
                    ].map((label) => (
                        <th key={label} className="teacher-header">{label}</th>
                    ))}</tr>
                </thead>
                <tbody>
                    {sectionPeriods.map(sp => (
                        <tr key={sp.id}>
                            <td className="time-slot" style={{ whiteSpace: 'nowrap' }}>
                                {editingPeriod === sp.id ? (
                                    <div>
                                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                                            <label key={day}>
                                                <input
                                                    type="checkbox"
                                                    checked={daysOfWeek.includes(day)}
                                                    onChange={() => handleDaysOfWeekChange(day)}
                                                />
                                                {day}
                                            </label>
                                        ))}
                                    </div>
                                ) : Array.isArray(sp.days_of_week) && sp.days_of_week.length > 0
                                    ? sp.days_of_week.join(', ')
                                    : 'No days selected'}
                            </td>
                            <td className="time-slot" style={{ whiteSpace: 'nowrap' }}>
                                {editingPeriod === sp.id ? (
                                    <input
                                        type="time"
                                        value={startTime}
                                        onChange={(e) => setStartTime(e.target.value)}
                                    />
                                ) : sp.start_time}
                            </td>
                            <td className="time-slot" style={{ whiteSpace: 'nowrap' }}>
                                {editingPeriod === sp.id ? (
                                    <input
                                        type="time"
                                        value={endTime}
                                        onChange={(e) => setEndTime(e.target.value)}
                                    />
                                ) : sp.end_time}
                            </td>
                            <td className="time-slot"> {/* Period Type Column */}
                                {editingPeriod === sp.id ? (
                                    <select
                                        value={periodType}
                                        onChange={(e) => setPeriodType(e.target.value)}
                                    >
                                        <option value="regular">Regular Class Period</option>
                                        <option value="homeroom">Homeroom</option>
                                        <option value="cte_flexible">CTE Flexible Block</option>
                                    </select>
                                ) : (
                                    <span>
                                        {sp.period_type === 'regular' && 'Regular Class Period'}
                                        {sp.period_type === 'homeroom' && 'Homeroom'}
                                        {sp.period_type === 'cte_flexible' && 'CTE Flexible Block'}
                                        {!sp.period_type && 'Regular Class Period'}
                                    </span>
                                )}
                            </td>
                            <td className="time-slot"> {/* Homeroom Column */}
                                {editingPeriod === sp.id ? (
                                    <input
                                        type="checkbox"
                                        checked={homeroom}
                                        onChange={(e) => setHomeroom(e.target.checked)}
                                    />
                                ) : sp.homeroom ? 'Yes' : 'No'}
                            </td>
                            <td className="time-slot">
                                {editingPeriod === sp.id ? (
                                    <button onClick={(e) => handleEditSubmit(e, sp.id)}>Save</button>
                                ) : (
                                    <>
                                        <button onClick={() => startEdit(sp)}>Edit</button>
                                        <button onClick={() => deleteSectionPeriod(sp.id)}>Delete</button>
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </div>
    );
}

// Helper function to get CSRF token
function getCSRFToken() {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'csrftoken') return value;
    }
    return null;
}

export default SectionPeriodList;
